import { useSyncExternalStore } from 'react';
import { fetchGlossary, type GlossaryTerm } from '@/lib/api';
import { buildGlossaryIndex, lookupGlossary, type GlossaryIndexOf } from '@/lib/glossaryIndex';

/* SPEC-453 — the glossary the SCREEN reads: ONE fetch, ONE shared snapshot.
 *
 * Param's ask: "people ask what it actually means" — so a ribbon label, a headline figure and
 * every state pill carry a ⓘ that answers in place. Those affordances are everywhere,
 * including inside table rows, so this hook is on a HOT path: a thousand-row table can mount
 * thousands of consumers.
 *
 * It is therefore a module-level store read through `useSyncExternalStore`, NOT per-component
 * state. Each consumer costs one Set entry and reads the SAME snapshot object; with
 * per-component state every pill would hold its own copy of the index and run its own effect.
 * `getSnapshot` MUST return a stable reference or React re-renders forever, which is why
 * `snapshot` is a module variable replaced only when the data actually changes.
 *
 * The lookup rules themselves are pure and tested in `lib/glossaryIndex.ts` — resolving a key
 * to the WRONG entry is worse than resolving nothing, so that logic is not inlined here.
 *
 * There is NO local fallback copy of any meaning: a term the backend does not define shows no
 * ⓘ at all rather than a second, drifting definition (the one-source rule).
 */

export type GlossaryIndex = GlossaryIndexOf<GlossaryTerm>;

const EMPTY: GlossaryIndex = { byKey: new Map(), terms: [] };

let snapshot: GlossaryIndex = EMPTY;
let inFlight: Promise<GlossaryIndex> | null = null;
let loaded = false;
const listeners = new Set<() => void>();

function publish(next: GlossaryIndex): void {
  snapshot = next;
  listeners.forEach((fn) => fn());
}

/** Load the glossary once per session. A FAILURE IS NOT CACHED: the first consumer can mount
 *  before a token exists (or during a 401), and caching "empty" then would silence every ⓘ for
 *  the rest of the session. The next mount retries instead. */
export function loadGlossary(): Promise<GlossaryIndex> {
  if (loaded) return Promise.resolve(snapshot);
  if (!inFlight) {
    inFlight = fetchGlossary()
      .then((p) => {
        const next = buildGlossaryIndex(p.terms || []);
        loaded = true;
        inFlight = null;
        publish(next);
        return next;
      })
      .catch(() => {
        inFlight = null;  // retry on a later mount rather than cache the failure
        return EMPTY;
      });
  }
  return inFlight;
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  // The first consumer that actually mounts starts the one fetch, so a page with no ⓘ on it
  // requests nothing.
  void loadGlossary();
  return () => { listeners.delete(onChange); };
}

function getSnapshot(): GlossaryIndex {
  return snapshot;
}

/** Test seam only — resets the session cache. Never called by page code. */
export function resetGlossaryCache(): void {
  snapshot = EMPTY;
  inFlight = null;
  loaded = false;
  listeners.forEach((fn) => fn());
}

export function useGlossary(): GlossaryIndex {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** The entry for a term or state token, or null when the glossary does not define it. */
export function lookupTerm(index: GlossaryIndex, key: string | null | undefined): GlossaryTerm | null {
  return lookupGlossary(index, key);
}
